import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Settings } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'autohaus_wilke_cookie_consent';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent flash on page load
      setTimeout(() => setIsVisible(true), 500);
    }
  }, []);

  const saveConsent = (consentData) => {
    const consentRecord = {
      ...consentData,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentRecord));
    setIsVisible(false);
    
    // Trigger analytics if accepted
    if (consentData.analytics) {
      // Enable analytics here if needed
      console.log('Analytics cookies accepted');
    }
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptEssentialOnly = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={() => {}}
      />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                <Cookie className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Cookie-Einstellungen</h3>
                <p className="text-gray-300 text-sm">Datenschutz ist uns wichtig</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {!showDetails ? (
              <>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Einige Cookies sind für den Betrieb der Website unerlässlich, während andere uns helfen, 
                  die Website zu verbessern und Ihnen personalisierte Inhalte anzuzeigen.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <button
                    onClick={acceptAll}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                    data-testid="cookie-accept-all"
                  >
                    Alle akzeptieren
                  </button>
                  <button
                    onClick={acceptEssentialOnly}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-all duration-300"
                    data-testid="cookie-reject-optional"
                  >
                    Nur essenzielle
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="flex-1 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="cookie-settings"
                  >
                    <Settings className="w-4 h-4" />
                    Einstellungen
                  </button>
                </div>

                <p className="text-sm text-gray-500">
                  Weitere Informationen finden Sie in unserer{' '}
                  <a href="/datenschutz.html" className="text-red-600 hover:underline">
                    Datenschutzerklärung
                  </a>
                </p>
              </>
            ) : (
              <>
                {/* Detailed Settings */}
                <div className="space-y-4 mb-6">
                  {/* Essential Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Essenzielle Cookies</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                          <div className="w-4 h-4 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BarChart3 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Analyse-Cookies</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem sie Informationen anonym sammeln und melden.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                        className="flex-shrink-0"
                        data-testid="cookie-toggle-analytics"
                      >
                        <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.analytics ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                          <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Cookie className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Marketing-Cookies</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Diese Cookies werden verwendet, um Werbung relevanter für Sie zu gestalten. Sie können auch die Häufigkeit begrenzen, mit der Sie eine Anzeige sehen.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                        className="flex-shrink-0"
                        data-testid="cookie-toggle-marketing"
                      >
                        <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.marketing ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                          <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="flex-1 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300"
                  >
                    Zurück
                  </button>
                  <button
                    onClick={savePreferences}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                    data-testid="cookie-save-preferences"
                  >
                    Einstellungen speichern
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
